import React from 'react';
import { InView } from 'react-intersection-observer';

import VerticalAdvertisement from '@components/FeaturedPartnerAd/Vertical';
import BrandPartnershipsBanner from '@components/Images/Rebranding/BrandPartnershipBanner';
import GroupSubscriptionBanner from '@components/Images/Rebranding/GroupSubscriptionBanner';
import RecommendedArticles from '@components/SideFeedItems/RecommendedArticles';
import useEventTracking from '@hooks/useEventTracking';
import {
  Advertisement,
  Article,
  CommunityComment,
  FeaturedPartner,
  LatestProArticles,
  Newsletter,
  UpcomingEvent,
} from '@interfaces/homepage';
import { corporate_landing_path, partners_list_path } from '@routes';
import { ClickTarget, ImpressionElement, TrackingLocation, click, impression } from '@tracking';

import CommunityComments from '../../SideFeedItems/CommunityComments';
import FeaturedPartners from '../../SideFeedItems/FeaturedPartners';
import LatestInPro from '../../SideFeedItems/LatestInPro';
import SubscribeToTheBriefing from '../../SideFeedItems/SubscribeToTheBriefing';
import UpcomingEvents from '../../SideFeedItems/UpcomingEvents';

interface SideFeedProps {
  advertisement: Advertisement;
  briefingNewsletter: Newsletter;
  communityComments: CommunityComment[];
  featuredPartners?: FeaturedPartner[];
  latestProArticles: LatestProArticles[];
  recommendedArticles?: Article[];
  upcomingEvents?: UpcomingEvent[];
}

const SideFeed = ({
  advertisement,
  briefingNewsletter,
  communityComments,
  featuredPartners,
  latestProArticles,
  recommendedArticles,
  upcomingEvents,
}: SideFeedProps) => {
  const { trackEvent } = useEventTracking({ location: TrackingLocation.SideFeed });

  return (
    <>
      {recommendedArticles?.length > 0 && (
        <RecommendedArticles recommendedArticles={recommendedArticles} />
      )}
      {briefingNewsletter && <SubscribeToTheBriefing briefingNewsletter={briefingNewsletter} />}
      <InView
        triggerOnce={true}
        onChange={(inView) => {
          inView &&
            trackEvent(
              impression({ impressionElement: ImpressionElement.CorporateSubscriptionAd })
            );
        }}
      >
        <a
          onClickCapture={() =>
            trackEvent(click({ clickTarget: ClickTarget.CorporateSubscriptionAd }))
          }
          href={corporate_landing_path()}
          className="mb-64 flex w-full"
          data-testid="group-subscription-image"
        >
          <GroupSubscriptionBanner className="w-full" />
        </a>
      </InView>
      {upcomingEvents?.length > 0 && <UpcomingEvents upcomingEvents={upcomingEvents} />}
      {featuredPartners?.length > 0 && <FeaturedPartners featuredPartners={featuredPartners} />}
      {advertisement && (
        <VerticalAdvertisement
          advertisement={advertisement}
          trackingLocation={TrackingLocation.SideFeed}
        />
      )}
      {latestProArticles?.length > 0 && <LatestInPro latestProArticles={latestProArticles} />}
      <InView
        triggerOnce={true}
        onChange={(inView) => {
          inView &&
            trackEvent(impression({ impressionElement: ImpressionElement.BrandPartnershipsAd }));
        }}
      >
        <a
          onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.BrandPartnershipAd }))}
          href={partners_list_path()}
          className="mb-64 flex aspect-[1/1] w-full"
          data-testid="brand-partnership-image"
        >
          <BrandPartnershipsBanner className="w-full" />
        </a>
      </InView>
      {communityComments?.length > 0 && <CommunityComments communityComments={communityComments} />}
    </>
  );
};

export default SideFeed;
